import { images } from './site'

export type Solution = {
  slug: string
  title: string
  lead: string
  body: string[]
  points: string[]
  image?: string
  imageAlt?: string
}

export const solutions: Solution[] = [
  {
    slug: 'rails-manuels',
    title: 'Rails à rideaux manuels',
    lead:
      'Un mouvement fluide, silencieux, obtenu sans moteur ni électronique : le rail manuel reste la base de tout projet de rideaux bien pensé.',
    body: [
      'Un rail manuel doit se faire oublier. Le rideau part au premier geste, glisse sans à-coups jusqu’au bout de la course et s’arrête là où on le laisse. C’est ce comportement, apparemment simple, qui demande le plus de précision : profil d’aluminium régulier, chariots correctement dimensionnés, fixations posées dans le bon axe.',
      'Nos rails à rideaux manuels s’installent au plafond comme au mur, en applique ou en encastrement dans un faux plafond. Ils acceptent les tissus légers en voilage comme les doublures occultantes plus lourdes, sur des largeurs importantes, en simple ou double rang lorsque le projet demande de superposer voilage et rideau.',
      'C’est la solution que nous recommandons pour les pièces où l’on manipule les rideaux occasionnellement, pour les projets où l’alimentation électrique n’est pas prévue, et pour les rénovations dans lesquelles il faut intervenir sans reprendre les plafonds.',
    ],
    points: [
      'Pose au plafond, au mur ou en faux plafond',
      'Simple ou double rang, voilage et rideau superposés',
      'Coupes et cintrages selon le plan du projet',
      'Accessoires de finition assortis au rail',
    ],
    image: images.railManual,
    imageAlt: 'Profil de rail à rideau manuel blanc équipé de ses chariots de glissement',
  },
  {
    slug: 'rails-electriques',
    title: 'Rail à rideau électrique',
    lead:
      'Ouverture et fermeture commandées, sans contact avec le tissu : le rail à rideau électrique protège les textiles et simplifie l’usage quotidien.',
    body: [
      'Dès que les hauteurs augmentent ou que les baies deviennent difficiles d’accès, la commande électrique change la vie des utilisateurs. Le rideau n’est plus tiré à la main, il est entraîné par le rail lui-même : le tissu n’est plus tordu, les ourlets ne sont plus salis, et la course reste identique jour après jour.',
      'Un rail à rideau électrique se prépare en amont. Il faut décider du côté d’arrivée de l’alimentation, du point de commande, de la place réservée dans le plafond ou le coffre, et du sens d’ouverture. Nous travaillons ces points avec les architectes et les bureaux d’études au moment des plans, afin que la pose se fasse ensuite sans reprise.',
      'C’est une solution particulièrement adaptée aux grandes hauteurs, aux salles de réunion, aux chambres d’hôtel, aux séjours en double hauteur et à toutes les situations où le confort d’usage compte autant que l’esthétique.',
    ],
    points: [
      'Course régulière et respect du tissu',
      'Alimentation et commande définies en phase projet',
      'Adapté aux grandes hauteurs et baies filantes',
      'Intégration discrète en faux plafond ou en coffre',
    ],
    image: images.sheerWhite,
    imageAlt: 'Intérieur clair équipé de voilages toute hauteur sur rail à rideau électrique',
  },
  {
    slug: 'rails-motorises',
    title: 'Rail rideau motorisé et pilotage',
    lead:
      'Un rail rideau motorisé s’intègre aux usages du bâtiment : commande murale, télécommande ou scénarios pilotés par la domotique du projet.',
    body: [
      'La motorisation n’a de sens que si elle est pilotée simplement. Selon le projet, la commande peut rester locale — un interrupteur près de la porte, une télécommande dans la chambre — ou être raccordée au système du bâtiment pour suivre des scénarios d’occupation, de lumière ou d’horaires.',
      'Dans l’hôtellerie, cela permet d’associer l’ouverture des rideaux à l’arrivée du client. Dans les bureaux et les salles de conférence, de fermer une rangée entière de baies avant une projection. Dans le résidentiel haut de gamme, de rassembler plusieurs pièces dans une seule commande cohérente.',
      'Nous cadrons systématiquement les interfaces avec l’électricien et l’intégrateur avant la commande du matériel : nature de la commande, emplacement des points de pilotage, regroupement des rails par zone. C’est ce travail préalable qui évite les mauvaises surprises en fin de chantier.',
    ],
    points: [
      'Commande murale, télécommande ou pilotage centralisé',
      'Regroupement des rails par zone ou par façade',
      'Coordination avec l’électricien et l’intégrateur',
      'Rails droits ou cintrés selon la géométrie des baies',
    ],
    image: images.railMotor,
    imageAlt: 'Rail rideau motorisé cintré avec son bloc moteur intégré',
  },
  {
    slug: 'rails-professionnels',
    title: 'Rail à rideau professionnel',
    lead:
      'Pour les lieux traversés par des centaines de personnes, le rail à rideau professionnel est conçu pour la manipulation répétée et l’entretien facile.',
    body: [
      'Un rideau dans un espace public n’est pas manipulé deux fois par jour mais parfois vingt. Les exigences changent : le profil doit rester rigide sur de grandes portées, les chariots doivent supporter des cycles nombreux, et le démontage du tissu doit pouvoir être fait rapidement par une équipe technique ou une blanchisserie.',
      'Nous privilégions donc des systèmes robustes, aux composants remplaçables, avec une logique de maintenance claire. L’objectif est simple : que l’exploitant puisse entretenir l’installation pendant des années sans devoir remplacer l’ensemble du rail.',
      'Bureaux, établissements scolaires, salles polyvalentes, espaces d’accueil, résidences gérées : partout où l’usage est intensif, ce niveau d’équipement est le bon point de départ.',
    ],
    points: [
      'Conçu pour une manipulation intensive',
      'Composants remplaçables et maintenance simple',
      'Grandes portées et rideaux lourds',
      'Dépose et repose rapides du textile',
    ],
    image: images.railMulti,
    imageAlt: 'Système de rails à rideaux multiples en aluminium pour usage professionnel',
  },
  {
    slug: 'rails-hopitaux',
    title: 'Rails pour hôpitaux et établissements de santé',
    lead:
      'Séparation des lits, intimité des patients, nettoyage régulier des textiles : les rails pour hôpitaux répondent à des contraintes d’exploitation précises.',
    body: [
      'Dans une chambre d’hôpital, une salle de soins ou un service d’urgence, le rideau est un élément fonctionnel. Il doit se déplacer d’une main, tenir en position, et pouvoir être décroché sans outil lorsque le textile part au lavage. Le rail, lui, doit accepter un nettoyage fréquent des surfaces autour de lui.',
      'Nous travaillons ces installations avec les services techniques : tracé des rails autour des lits, prise en compte des bras d’équipement médical, des potences et des rideaux de séparation, gestion des angles et des retours pour préserver l’intimité tout en laissant le passage libre.',
      'La géométrie compte autant que le matériel. Un tracé bien étudié permet de fermer complètement un espace sans gêner l’accès du personnel — c’est la partie la plus utile de notre travail sur les projets de santé.',
    ],
    points: [
      'Tracés en L, en U et angles pour la séparation des lits',
      'Décrochage du textile sans outil pour le lavage',
      'Prise en compte des équipements médicaux',
      'Systèmes pensés pour un entretien fréquent',
    ],
    image: images.railMulti,
    imageAlt: 'Rails à rideaux en aluminium destinés aux établissements de santé',
  },
  {
    slug: 'rails-hotels',
    title: 'Rails pour hôtels et espaces commerciaux',
    lead:
      'Des rails pour hôtels qui tiennent la cadence de l’exploitation tout en gardant le niveau de finition attendu dans un projet hôtelier.',
    body: [
      'Une chambre d’hôtel demande deux choses en même temps : un confort immédiatement lisible pour le client et une installation qui résiste à un rythme d’usage soutenu. Le rideau doit occulter réellement, glisser sans bruit la nuit, et rester impeccable après des centaines de séjours.',
      'Nous accompagnons les groupes hôteliers, les architectes d’intérieur et les entreprises générales sur les chambres pilotes, puis sur le déploiement à l’identique dans l’ensemble de l’établissement. Le double rang voilage plus occultant, la motorisation des baies principales et l’intégration dans le faux plafond sont les configurations les plus fréquentes.',
      'Dans le commerce et la restauration, les mêmes principes s’appliquent avec un objectif différent : séparer des espaces, filtrer la lumière d’une vitrine, ou fermer une salle privatisable sans construire de cloison.',
    ],
    points: [
      'Chambre pilote puis déploiement homogène',
      'Double rang voilage et occultant',
      'Motorisation des baies principales',
      'Séparation d’espaces en commerce et restauration',
    ],
    image: images.seaView,
    imageAlt: 'Séjour d’hôtel avec vue mer, voilages blancs et rideaux rouges sur rails',
  },
  {
    slug: 'rails-sur-mesure',
    title: 'Rail à rideau sur mesure',
    lead:
      'Baies cintrées, angles, îlots, plafonds inclinés : le rail à rideau sur mesure est fabriqué pour la géométrie réelle du projet.',
    body: [
      'Beaucoup de projets sortent des formes standard. Une baie en arc, une cloison arrondie, un lit central à entourer, un plafond qui ne suit pas l’horizontale : dans ces cas, le rail doit être dessiné avant d’être fabriqué.',
      'Nous partons des plans ou d’un relevé sur site, puis nous définissons le développé du rail, les rayons de cintrage, les points de fixation et les jonctions. La commande est ensuite lancée sur cette base, avec les longueurs et les cintrages nécessaires, afin que la pose consiste à installer — pas à improviser.',
      'C’est aussi la voie à privilégier quand le rail doit disparaître : gorge lumineuse, retombée de plafond, coffre menuisé. Le rideau semble alors sortir directement du plafond, sans aucune pièce visible.',
    ],
    points: [
      'Étude du développé et des rayons de cintrage',
      'Relevé sur site ou travail sur plans d’exécution',
      'Intégration invisible en gorge ou en coffre',
      'Jonctions et fixations définies avant fabrication',
    ],
    image: images.salonBeige,
    imageAlt: 'Grand salon aux rideaux beiges installés sur un rail sur mesure suivant la courbe du plafond',
  },
]

export const applications = [
  {
    title: 'Architectes et maîtres d’œuvre',
    text:
      'Choix du système, réservations en plafond, tracés et détails techniques cadrés dès la phase de conception, pour éviter les arbitrages en fin de chantier.',
  },
  {
    title: 'Architectes d’intérieur',
    text:
      'Rails discrets ou totalement dissimulés, doubles rangs, finitions cohérentes avec le projet décoratif et le tombé du tissu.',
  },
  {
    title: 'Hôtellerie',
    text:
      'Chambres, suites et espaces communs : occultation efficace, fonctionnement silencieux et déploiement homogène sur tout l’établissement.',
  },
  {
    title: 'Santé',
    text:
      'Chambres, salles de soins et services techniques : séparation des lits, textiles déposables pour le lavage, tracés adaptés aux équipements.',
  },
  {
    title: 'Tertiaire et commerce',
    text:
      'Bureaux, salles de réunion, showrooms et espaces d’accueil : gestion de la lumière, séparation d’espaces et usage intensif.',
  },
  {
    title: 'Résidentiel haut de gamme',
    text:
      'Villas et appartements : grandes baies, motorisation intégrée aux usages de la maison et finitions soignées jusqu’au dernier détail.',
  },
]
